import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Client, Environment } from '@zappar/zw-api-client';
import { EchoClient, type Comment, type CommentCreatedEvent } from './client';
import type { SystemMessage } from './systemMessages';
import type { IAsset } from '../providers';

const toEchoEnv = (env: Environment): 'DEVELOPMENT' | 'STAGING' | 'PRODUCTION' => {
  if (env === Environment.Staging) return 'STAGING';
  if (env === Environment.Prod) return 'PRODUCTION';
  return 'DEVELOPMENT';
};

function assetToResourceId(asset: IAsset): string {
  return `zml:${asset.getId()}`;
}

/**
 * Coalesce window per `coalesceKey`, in seconds. One window per key — every
 * system comment that shares a coalesce key shares the window, so siblings can't
 * drift (e.g. `moved-to-bin` and `restored-from-bin` both bind to `bin` → 300s).
 * Keys not listed here fall back to `DEFAULT_COALESCE_WINDOW_SEC`.
 *
 * Window-tuning audit table — edit here, not at call sites.
 */
export const COALESCE_WINDOWS_SEC: Record<string, number> = {
  caption: 60,           // rename-fiddle is bursty; surviving body still shows the true original via rewriteBody
  'workflow-status': 60, // status dropdowns are usually one deliberate click; 2 min covers immediate "wrong status, fix it"
  labels: 60,             // chip toggles fire many micro-edits; 1 min absorbs the burst without folding distinct sessions
  bin: 30,               // "oops, restore" can take minutes (navigate to bin, find, restore); high-regret action
  'version-upload': 60,  // multi-file upload batches finish in a few minutes
};

const DEFAULT_COALESCE_WINDOW_SEC = 120;

/** Options for coalescing same-kind system comments within a short window. */
export interface PostSystemCommentOptions {
  /**
   * Collapse this comment with any prior on the same asset that shares this key,
   * if posted within the window defined in `COALESCE_WINDOWS_SEC`. The prior is
   * deleted before the new one posts, so only the latest row remains.
   */
  coalesceKey?: string;
  /**
   * `'replace'` (default): delete prior, post new.
   * `'cancel'`: if a prior with `cancelKey` exists in window, delete it and post
   * nothing — used for bin toggle (moved-to-bin then restored cancels both out).
   */
  mode?: 'replace' | 'cancel';
  /** In `'cancel'` mode, the `coalesceKey` of the prior to look for. */
  cancelKey?: string;
  /**
   * Rewrite the body before posting, given the prior message. Lets the caller
   * preserve context across a burst — e.g. caption-changed keeps the very first
   * `originalFrom` across an A→B→C rename burst by reading `prior.data`.
   * Return `null` to drop the prior and post nothing (use when the burst nets
   * out to no change — e.g. rename A→B→A or label add-X-then-remove-X).
   */
  rewriteBody?: (prior: SystemMessage) => SystemMessage | null;
}

export interface EchoContextValue {
  isConnected: boolean;
  error: string | null;
  /**
   * Post a system comment for any asset by its ID. Pass the result of a body
   * builder from `systemMessages.ts` — the event key on that object is sent on
   * the wire as `kind: "system:${eventKey}"`.
   * Safe to call while still connecting — messages are queued and flushed once the
   * WebSocket is open.
   * Pass `options` to coalesce against a recent same-kind comment on the same asset.
   */
  postSystemComment: (assetId: string, message: SystemMessage, options?: PostSystemCommentOptions) => void;
  /** Comments for the currently subscribed asset. */
  comments: Comment[];
  /** True while the initial page of comments is being fetched. */
  isLoadingComments: boolean;
  /** The resourceId (`zml:{assetId}`) that CommentsTab is currently subscribed to. */
  subscribedAssetId: string | null;
  /** Subscribe to real-time comment updates for an asset. Clears previous subscription. */
  subscribeToAsset: (asset: IAsset) => void;
  /** Unsubscribe from real-time comment updates and clear comment state. */
  unsubscribeFromAsset: (asset: IAsset) => void;
  /**
   * Post a user comment to the currently subscribed asset. The returned promise resolves
   * when the server echoes back a matching `commentCreated` event (matched on resourceId
   * and body — the wire protocol has no correlation ID). Rejects on a 10-second timeout.
   */
  postComment: (body: string) => Promise<void>;
  /** Delete a comment from the currently subscribed asset. Optimistically removes it from state. */
  deleteComment: (commentId: string) => void;
}

const EchoContext = createContext<EchoContextValue>({
  isConnected: false,
  error: null,
  postSystemComment: () => {},
  comments: [],
  isLoadingComments: false,
  subscribedAssetId: null,
  subscribeToAsset: () => {},
  unsubscribeFromAsset: () => {},
  postComment: () => Promise.resolve(),
  deleteComment: () => {},
});

export const useEchoContext = (): EchoContextValue => useContext(EchoContext);

interface EchoProviderProps {
  zwApiClient: Client;
  workspaceId: string;
  children: React.ReactNode;
}

export const EchoProvider = ({ zwApiClient, workspaceId, children }: EchoProviderProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [subscribedAssetId, setSubscribedAssetId] = useState<string | null>(null);

  const echoRef = useRef<EchoClient | null>(null);
  /** Keeps the current subscribed resourceId accessible inside event handlers without stale closures. */
  const subscribedResourceIdRef = useRef<string | null>(null);
  /** System messages queued before the WebSocket connection is open. */
  const pendingSystemMessagesRef = useRef<Array<{ resourceId: string; message: SystemMessage }>>([]);
  /**
   * Coalesce ledger: tracks recently-posted system comments so subsequent calls with
   * the same `(assetId, coalesceKey)` can delete the prior server-side and replace it.
   * Session-scoped — a page reload wipes the ledger; that's acceptable.
   */
  const systemLedgerRef = useRef<Array<{
    assetId: string;
    resourceId: string;
    coalesceKey: string;
    message: SystemMessage;
    /** Back-filled when the server echoes commentCreated; until then, suppress-only on delete. */
    commentId: string | null;
    /** Used to match the server echo (the wire protocol has no correlation ID). */
    pendingBody: string;
    postedAt: number;
    /**
     * True when a coalesce has already orphaned this entry (a later same-key
     * post wants it gone) but the server echo hasn't arrived yet, so we don't
     * know the `commentId` to delete. The entry stays in the ledger as a
     * tombstone — invisible to future coalesce searches — until back-fill, at
     * which point the delete fires and the entry is removed.
     */
    pendingDelete?: boolean;
  }>>([]);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;

    const resolveToken = async (): Promise<string | null> => {
      const existing = zwApiClient.getAccessToken();
      if (existing) return existing;
      if (zwApiClient.useCookieAuth) {
        const resp = await zwApiClient.apiRequest<{ token: string }>('/echo-token/', {}, 'POST');
        return resp.token ?? null;
      }
      return null;
    };

    const setup = async () => {
      let accessToken: string | null;
      try {
        accessToken = await resolveToken();
      } catch {
        if (!cancelled) setError('Failed to obtain a comments session token.');
        return;
      }
      if (cancelled) return;

      if (!accessToken) {
        setError('Comments are unavailable for this session (missing access token).');
        return;
      }

      const cmsEnv = toEchoEnv(zwApiClient.env);
      const useDev = zwApiClient.env === Environment.Local || zwApiClient.env === Environment.Dev || zwApiClient.env === Environment.Staging;

      const client = new EchoClient({
        accessToken,
        cmsEnv,
        // Workspace-scoped connection: authorises the client to subscribe to and
        // post comments on any asset within this workspace.
        resourceId: `workspace:${workspaceId}`,
        workspaceId,
        useDev,
      });
      echoRef.current = client;

      const mergeComments = (incoming: Comment[], forResourceId: string) => {
        if (forResourceId !== subscribedResourceIdRef.current) return;
        setComments(prev => {
          const merged = new Map(prev.map(c => [c.commentId, c]));
          incoming.forEach(c => merged.set(c.commentId, c));
          // Newest first — CommentsTab renders the list top-down with the most recent comment at the top.
          return Array.from(merged.values()).sort((a, b) => b.createdAt - a.createdAt);
        });
      };

      client
        .on('open', () => {
          if (cancelled) return;
          setIsConnected(true);
          setError(null);

          // If CommentsTab already called subscribeToAsset before the socket opened, catch up now.
          const currentResourceId = subscribedResourceIdRef.current;
          if (currentResourceId) {
            client.subscribe(currentResourceId);
            client.listComments({ resourceId: currentResourceId, limit: 100 });
          }

          // Flush system messages that arrived before the connection was ready.
          const pending = pendingSystemMessagesRef.current.splice(0);
          for (const { resourceId, message } of pending) {
            client.postComment(resourceId, message.body, { kind: `system:${message.eventKey}` });
          }
        })
        .on('close', () => {
          if (!cancelled) setIsConnected(false);
        })
        .on('error', () => {
          if (!cancelled) setError('Failed to connect to comments service.');
        })
        .on('listCommentsResult', (data) => {
          if (cancelled) return;
          mergeComments(data.items ?? [], data.resourceId);
          setIsLoadingComments(false);
        })
        .on('commentCreated', (data) => {
          if (cancelled) return;
          mergeComments([data.comment], data.resourceId);

          // Back-fill the ledger so a subsequent coalesce can delete this comment server-side.
          const kind = data.comment.kind;
          if (typeof kind === 'string' && kind.startsWith('system:')) {
            const eventKey = kind.slice('system:'.length);
            const entry = systemLedgerRef.current.find(
              e => e.commentId === null
                && e.message.eventKey === eventKey
                && e.pendingBody === data.comment.body,
            );
            if (entry) {
              entry.commentId = data.comment.commentId;
              // If a coalesce already orphaned this entry while it was awaiting echo,
              // fire the deferred server-side delete now and remove the tombstone.
              if (entry.pendingDelete) {
                const echoClient = echoRef.current;
                if (echoClient?.connected) {
                  echoClient.deleteComment(entry.resourceId, entry.commentId);
                  setComments(prev => prev.filter(c => c.commentId !== entry.commentId));
                }
                systemLedgerRef.current = systemLedgerRef.current.filter(e => e !== entry);
              }
            }
          }
        })
        .on('commentsDeleted', (data) => {
          if (cancelled) return;
          if (data.resourceId !== subscribedResourceIdRef.current) return;
          const deletedIds = new Set(data.commentIds);
          setComments(prev => prev.filter(c => !deletedIds.has(c.commentId)));
        });

      client.connect();
    };

    setup();

    return () => {
      cancelled = true;
      const client = echoRef.current;
      if (client) {
        const currentResourceId = subscribedResourceIdRef.current;
        if (currentResourceId) {
          try { client.unsubscribe(currentResourceId); } catch { /* ignore cleanup errors */ }
        }
        client.disconnect();
        echoRef.current = null;
      }
    };
  }, [zwApiClient, workspaceId]);

  const postSystemComment = useCallback((
    assetId: string,
    message: SystemMessage,
    options?: PostSystemCommentOptions,
  ) => {
    const resourceId = `zml:${assetId}`;
    const client = echoRef.current;
    const now = Date.now();
    // GC entries older than the longest window any caller uses.
    const maxWindowSec = Math.max(DEFAULT_COALESCE_WINDOW_SEC, ...Object.values(COALESCE_WINDOWS_SEC));
    systemLedgerRef.current = systemLedgerRef.current.filter(e => now - e.postedAt < maxWindowSec * 1000);

    const mode = options?.mode ?? 'replace';
    const matchKey = mode === 'cancel' ? options?.cancelKey : options?.coalesceKey;
    const windowSec = (matchKey && COALESCE_WINDOWS_SEC[matchKey] != null)
      ? COALESCE_WINDOWS_SEC[matchKey]
      : DEFAULT_COALESCE_WINDOW_SEC;
    const windowMs = windowSec * 1000;
    const prior = matchKey
      ? systemLedgerRef.current.find(e =>
          !e.pendingDelete
          && e.assetId === assetId
          && e.coalesceKey === matchKey
          && now - e.postedAt < windowMs)
      : undefined;

    const dropPrior = () => {
      if (!prior) return;
      if (prior.commentId && client?.connected) {
        // Echo already back — delete server-side immediately and drop the ledger entry.
        client.deleteComment(prior.resourceId, prior.commentId);
        // Mirror the optimistic local removal that the public deleteComment does.
        setComments(prev => prev.filter(c => c.commentId !== prior.commentId));
        systemLedgerRef.current = systemLedgerRef.current.filter(e => e !== prior);
      } else if (prior.commentId === null && client?.connected) {
        // Echo not back yet — mark as tombstone. The commentCreated handler
        // will fire the delete as soon as the server-assigned commentId arrives.
        prior.pendingDelete = true;
      } else {
        // Disconnected: prior is still in the pending queue (never reached the wire).
        // Pull it out so it never sends, and drop the ledger entry.
        pendingSystemMessagesRef.current = pendingSystemMessagesRef.current.filter(
          p => p.message !== prior.message,
        );
        systemLedgerRef.current = systemLedgerRef.current.filter(e => e !== prior);
      }
    };

    if (mode === 'cancel') {
      if (prior) {
        dropPrior();
        return;
      }
      // Fall through to a normal post when no prior matched.
    }

    let finalMessage = message;
    if (mode === 'replace' && prior) {
      if (options?.rewriteBody) {
        const rewritten = options.rewriteBody(prior.message);
        if (rewritten === null) {
          // Burst nets out to no change — wipe the prior and post nothing.
          dropPrior();
          return;
        }
        finalMessage = rewritten;
      }
      dropPrior();
    }

    systemLedgerRef.current.push({
      assetId,
      resourceId,
      coalesceKey: options?.coalesceKey ?? '__nocoalesce__',
      message: finalMessage,
      commentId: null,
      pendingBody: finalMessage.body,
      postedAt: now,
    });

    if (client?.connected) {
      client.postComment(resourceId, finalMessage.body, { kind: `system:${finalMessage.eventKey}` });
    } else {
      // Queue for when the connection opens.
      pendingSystemMessagesRef.current.push({ resourceId, message: finalMessage });
    }
  }, []);

  const subscribeToAsset = useCallback((asset: IAsset) => {
    const resourceId = assetToResourceId(asset);
    const client = echoRef.current;

    // Unsubscribe from the previous asset if switching.
    const prevResourceId = subscribedResourceIdRef.current;
    if (prevResourceId && prevResourceId !== resourceId && client?.connected) {
      try { client.unsubscribe(prevResourceId); } catch { /* ignore */ }
    }

    subscribedResourceIdRef.current = resourceId;
    setSubscribedAssetId(resourceId);
    setComments([]);
    setIsLoadingComments(true);

    if (client?.connected) {
      client.subscribe(resourceId);
      client.listComments({ resourceId, limit: 100 });
    }
    // If not yet connected, the `open` handler will subscribe automatically.
  }, []);

  const unsubscribeFromAsset = useCallback((asset: IAsset) => {
    const resourceId = assetToResourceId(asset);
    const client = echoRef.current;
    if (client?.connected) {
      try { client.unsubscribe(resourceId); } catch { /* ignore */ }
    }
    subscribedResourceIdRef.current = null;
    setSubscribedAssetId(null);
    setComments([]);
    setIsLoadingComments(false);
  }, []);

  const postComment = useCallback((body: string): Promise<void> => {
    const resourceId = subscribedResourceIdRef.current;
    const client = echoRef.current;
    if (!client?.connected || !resourceId) {
      return Promise.reject(new Error('EchoClient: not connected or no asset subscribed'));
    }
    return new Promise<void>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const cleanup = () => {
        if (timeoutId !== null) clearTimeout(timeoutId);
        client.off('commentCreated', onCreated);
      };
      const onCreated = (data: CommentCreatedEvent) => {
        if (data.resourceId !== resourceId) return;
        if (data.comment.body !== body) return;
        cleanup();
        resolve();
      };
      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Timed out waiting for comment confirmation'));
      }, 10_000);
      client.on('commentCreated', onCreated);
      try {
        client.postComment(resourceId, body);
      } catch (err) {
        cleanup();
        reject(err);
      }
    });
  }, []);

  const deleteComment = useCallback((commentId: string) => {
    const resourceId = subscribedResourceIdRef.current;
    const client = echoRef.current;
    if (!client?.connected || !resourceId) throw new Error('EchoClient: not connected or no asset subscribed');
    client.deleteComment(resourceId, commentId);
    // Optimistically remove from state; the server also sends a `commentsDeleted` event.
    setComments(prev => prev.filter(c => c.commentId !== commentId));
  }, []);

  return (
    <EchoContext.Provider value={{
      isConnected,
      error,
      postSystemComment,
      comments,
      isLoadingComments,
      subscribedAssetId,
      subscribeToAsset,
      unsubscribeFromAsset,
      postComment,
      deleteComment,
    }}>
      {children}
    </EchoContext.Provider>
  );
};
